import {
  Document,
  Page,
  View,
  StyleSheet,
  Font,
  Image,
} from "@react-pdf/renderer";
import Header from "./Header";
import Questions from "./questions/Questions";
import { PDFData, QuestionType } from "./types"; // Ensure correct imports
import { t } from "i18next";
import { QuestionTypeEnum } from "./enums";
import regular from "@packages/assets/font-family/Montserrat-Regular.ttf";
import bold from "@packages/assets/font-family/Montserrat-Bold.ttf";
import light from "@packages/assets/font-family/Montserrat-Light.ttf";
import italic from "@packages/assets/font-family/Montserrat-Italic.ttf";
import boldItalic from "@packages/assets/font-family/Montserrat-BoldItalic.ttf";

Font.register({
  family: "Montserrat",
  fonts: [
    {
      src: bold,
      format: "truetype",
      fontWeight: 700,
    },
    {
      src: regular,
      format: "truetype",
      fontWeight: 400,
    },
    {
      src: italic,
      format: "truetype",
      fontWeight: 400,
      fontStyle: "italic",
    },
    {
      src: boldItalic,
      format: "truetype",
      fontWeight: 700,
      fontStyle: "italic",
    },
    {
      src: light,
      fontWeight: 300,
      format: "truetype",
    },
  ],
});

const styles = StyleSheet.create({
  page: {
    flexDirection: "column",
    padding: 20,
    fontFamily: "Montserrat",
    gap: 10,
  },
  header: {
    marginBottom: 10,
  },
  assignmentImage: {
    width: "100%",
    height: "auto",
    maxHeight: "200px",
    objectFit: "contain",
    marginBottom: 20,
    alignSelf: "center",
    borderRadius: 4,
  },
  imageContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    marginBottom: 20,
  },
  rowsContainer: {
    flexDirection: "column",
  },
  row: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 10,
  },
  questionContainer: {
    flex: 1,
  },
  divider: {
    borderLeftWidth: 1,
    borderLeftColor: "#F4E3D0",
    marginLeft: 10,
    marginRight: 10,
    alignSelf: "stretch",
  },
});

interface PDFProps {
  data: PDFData;
  school?: string;
}

const PDFRenderer = ({ data, school }: PDFProps) => {
  if (!data.questions) return null;

  // Define the desired order of question types
  const order = ["OE", "TEST", "TF", "FB"];

  // Sort questions by the specified order and question id
  const sortedQuestions = [...data.questions]
    .sort((a, b) => {
      const typeOrder = order.indexOf(a.type) - order.indexOf(b.type);
      return typeOrder === 0 ? a.id - b.id : typeOrder;
    })
    .filter((q) => q.type !== ("MATCH" as QuestionTypeEnum)); // Remove MATCH questions

  // Group questions. OE questions take full width. Others get paired side-by-side.
  const groupedQuestions: QuestionType[][] = [];
  let i = 0;
  while (i < sortedQuestions.length) {
    const currentQ = sortedQuestions[i];
    if (currentQ.type === "OE") {
      // OE questions take full width alone
      groupedQuestions.push([currentQ]);
      i++;
    } else {
      // Attempt to pair with the next non-OE question
      if (
        i + 1 < sortedQuestions.length &&
        sortedQuestions[i + 1].type !== "OE"
      ) {
        groupedQuestions.push([currentQ, sortedQuestions[i + 1]]);
        i += 2;
      } else {
        // No suitable second question or next is OE, just push single
        groupedQuestions.push([currentQ]);
        i++;
      }
    }
  }

  let questionIndexCounter = 0;

  return (
    <Document
      title={data.title || t("pdf_renderer.homework")}
      author="madlen.io"
      creator="madlen.io"
      producer="madlen.io"
    >
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          <Header school={school} title={data.title || ""} />
        </View>

        {/* Assignment Image */}
        {data.assignmentImageUrl && (
          <View style={styles.imageContainer}>
            <Image
              src={data.assignmentImageUrl}
              style={styles.assignmentImage}
            />
          </View>
        )}

        <View style={styles.rowsContainer}>
          {groupedQuestions.map((pair, rowIndex) => {
            const isSingleOE = pair.length === 1 && pair[0].type === "OE";
            return (
              <View style={styles.row} key={`row-${rowIndex}`} wrap={false}>
                {/* Left or full-width question */}
                <View style={styles.questionContainer}>
                  <Questions
                    questions={[pair[0]]}
                    showAnswers={data.showAnswers}
                    questionIndex={questionIndexCounter++}
                  />
                </View>

                {/* If it's a single OE question, no divider or second column */}
                {!isSingleOE && (
                  <>
                    <View style={styles.divider} />
                    {pair.length === 2 ? (
                      <View style={styles.questionContainer}>
                        <Questions
                          questions={[pair[1]]}
                          showAnswers={data.showAnswers}
                          questionIndex={questionIndexCounter++}
                        />
                      </View>
                    ) : (
                      <View style={styles.questionContainer} />
                    )}
                  </>
                )}
              </View>
            );
          })}
        </View>
      </Page>
    </Document>
  );
};

export default PDFRenderer;
